<script setup>
import { computed } from 'vue';
import { getInboxIconByType, getInboxBrandColor } from 'dashboard/helper/inbox';
import wootConstants from 'dashboard/constants/globals';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const statusColorMap = {
  [wootConstants.AVAILABILITY_STATUS_KEYS[0]]: 'text-grass-500',
  [wootConstants.AVAILABILITY_STATUS_KEYS[1]]: 'text-n-amber-10',
  [wootConstants.AVAILABILITY_STATUS_KEYS[2]]: 'text-n-gray-6',
};

const statusColor = computed(() => {
  return statusColorMap[currentContactStatus.value] || 'text-n-gray-6';
});

const statusText = computed(() => {
  const status = 'ONLINE'
  return t(`CONTACTS_LAYOUT.DETAILS.AVAILABILITY.STATUSES_LIST.${status}`);
});

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { phoneNumber, channelType } = inbox.value;
  return getInboxIconByType(channelType, phoneNumber);
});

const inboxBrandColor = computed(() => {
  const { phoneNumber, channelType } = inbox.value;
  return getInboxBrandColor(channelType, phoneNumber);
});

const shouldShowInboxBadge = computed(() => {
  const { channelType } = inbox.value;
  // Hiển thị badge cho Zalo và các inbox khác có thể cần phân biệt
  return (
    channelType === 'Channel::ZaloPersonal' ||
    channelType === 'Channel::ZaloOa' ||
    channelType === 'Channel::Line' ||
    channelType === 'Channel::Telegram'
  );
});

const hasUnread = computed(() => {
  return props.conversation?.unreadCount > 0;
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { slaPolicyId, labels = [] } = props.conversation;
  return !slaPolicyId && labels.length === 0;
});

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.hichatConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    class="flex w-full gap-3 p-3 transition-all duration-300 ease-in-out cursor-pointer bg-slate-50 dark:bg-n-vars-black-br/30"
    :class="{
      'bg-slate-50 dark:bg-slate-800': hasUnread,
      'hover:bg-slate-50 dark:hover:bg-slate-800': !hasUnread,
    }"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="32"
      rounded-full
    >
      <template #badge="{ size }">
        <div v-if="shouldShowInboxBadge"
          class="absolute z-20 flex items-center justify-center rounded-full bg-n-slate-3 dark:bg-n-slate-4 border border-n-slate-3 dark:border-n-slate-4"
          :style="{
            width: `${(size * 0.4)}px`,
            height: `${(size * 0.4)}px`,
            top: `${size - (size * 0.4) / 1.1}px`,
            left: `${size - (size * 0.4) / 1.1}px`,
          }">
          <Icon :icon="inboxIcon" :style="{
            color: inboxBrandColor,
            width: `${(size * 0.25)}px`,
            height: `${(size * 0.25)}px`,
          }" />
        </div>
      </template>
    </Avatar>
    <div class="flex flex-col w-full gap-[6px]">
      <div class="flex justify-between gap-[6px]">
        <div class="flex flex-col">
          <span :class="[statusColor, 'text-xs']">
            {{ statusText }}
          </span>
          <div class="flex gap-3">
            <h4 class="text-base font-medium truncate" :class="{
            'text-n-gray-3 font-semibold': hasUnread,
            'text-n-gray-3': !hasUnread,
          }">
            {{ currentContactName }}
          </h4>
          <div class="flex items-center gap-1 text-xs text-n-gray-6">
            <i class="i-lucide-message-square"/>
            <span>{{conversation.unreadCount}}</span>
            <span>tin nhắn</span>
          </div>
          </div>
        </div>
        <div class="flex gap-2">
          <CardPriorityIcon :priority="conversation.priority || null" />
          <!-- <div v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5">
            <Icon :icon="inboxIcon" class="flex-shrink-0 text-n-slate-11 size-3" />
          </div> -->
          <span class="text-xs text-n-gray-6">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview v-if="showMessagePreviewWithoutMeta" :conversation="conversation" />
      <CardMessagePreviewWithMeta v-else :conversation="conversation" :account-labels="accountLabels" />
    </div>
  </div>
</template>
